import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";

import {
  ClientOfferTier,
  ClientOfferTouchpoint,
  ClientOfferType,
  type ClientOfferEntity,
} from "@fitness-app/data-models/entities/ClientOffer";

import { supabase } from "~/store/initializeStore";

// TRE-1613 (dopełnienie TRE-1611) — admin CRUD for client_offer: configurable upsell
// offers surfaced to clients in the mobile app (home / celebration / advisor / endOfTrial).

const offersQueryKey = ["clientOffers"];

type OfferFormValues = {
  title: string;
  description: string;
  price?: string;
  type: ClientOfferType;
  tier: ClientOfferTier;
  url: string;
  badge?: string;
  productIds?: string[];
  touchpoints?: string[];
  enabled: boolean;
  sortOrder: number;
  audienceJoinedFrom?: Dayjs | null;
  audienceJoinedTo?: Dayjs | null;
};

const offerTypeOptions = [
  { value: ClientOfferType.SelfCheckout, label: "Samodzielny zakup (self-checkout)" },
  { value: ClientOfferType.Consultation, label: "Konsultacja" },
];

// TRE-2181: step of the upgrade path (home stepper) and the Meta event sent on tap.
const offerTierOptions = [
  { value: ClientOfferTier.LowTicket, label: "Low ticket (Klucz -> Bikini), Meta: Lead" },
  { value: ClientOfferTier.HighTicket, label: "High ticket (Program Hollywood, konsultacja), Meta: LeadQualified" },
];

const touchpointOptions = [
  { value: ClientOfferTouchpoint.Home, label: "Ekran główny" },
  { value: ClientOfferTouchpoint.Celebration, label: "Celebracja" },
  { value: ClientOfferTouchpoint.Advisor, label: "Opiekunka" },
  { value: ClientOfferTouchpoint.EndOfTrial, label: "Koniec dostępu" },
];

const touchpointLabel = (value: string) => touchpointOptions.find((option) => option.value === value)?.label ?? value;

const toStringArray = (value: ClientOfferEntity["productIds"]): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];

export const AdvisorOffersTab = (): React.ReactElement => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm<OfferFormValues>();
  const [activeOffer, setActiveOffer] = useState<ClientOfferEntity | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const offersQuery = useQuery({
    queryKey: offersQueryKey,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_offer")
        .select("*")
        .order("sortOrder", { ascending: true })
        .order("title", { ascending: true });

      if (error) throw new Error(error.message);
      return (data ?? []) as ClientOfferEntity[];
    },
  });

  const productsQuery = useQuery({
    queryKey: ["clientOfferProducts"],
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase.from("product").select("id, name").order("name", { ascending: true });

      if (error) throw new Error(error.message);
      return (data ?? []) as { id: string; name: string }[];
    },
  });

  const products = productsQuery.data ?? [];
  const productNameById = new Map(products.map((product) => [product.id, product.name]));

  const saveOfferMutation = useMutation({
    mutationFn: async (values: OfferFormValues) => {
      const payload = {
        title: values.title,
        description: values.description ?? "",
        price: values.price?.trim() ? values.price.trim() : null,
        type: values.type,
        tier: values.tier,
        url: values.url,
        badge: values.badge?.trim() ? values.badge.trim() : null,
        productIds: values.productIds?.length ? values.productIds : null,
        touchpoints: values.touchpoints?.length ? values.touchpoints : null,
        enabled: values.enabled,
        sortOrder: Number(values.sortOrder ?? 0),
        audienceJoinedFrom: values.audienceJoinedFrom ? values.audienceJoinedFrom.startOf("day").toISOString() : null,
        audienceJoinedTo: values.audienceJoinedTo ? values.audienceJoinedTo.endOf("day").toISOString() : null,
        updatedAt: new Date().toISOString(),
      };

      if (activeOffer) {
        const { error } = await supabase.from("client_offer").update(payload).eq("id", activeOffer.id);
        if (error) throw new Error(error.message);
        return;
      }

      const { error } = await supabase.from("client_offer").insert(payload);
      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      setEditorOpen(false);
      setActiveOffer(null);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: offersQueryKey });
      void message.success("Oferta zapisana");
    },
    onError: (error) => {
      void message.error(error instanceof Error ? error.message : "Nie udało się zapisać oferty");
    },
  });

  const toggleOfferMutation = useMutation({
    mutationFn: async ({ offer, enabled }: { offer: ClientOfferEntity; enabled: boolean }) => {
      const { error } = await supabase
        .from("client_offer")
        .update({ enabled, updatedAt: new Date().toISOString() })
        .eq("id", offer.id);

      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: offersQueryKey });
    },
    onError: (error) => {
      void message.error(error instanceof Error ? error.message : "Nie udało się zmienić statusu oferty");
    },
  });

  const deleteOfferMutation = useMutation({
    mutationFn: async (offer: ClientOfferEntity) => {
      const { error } = await supabase.from("client_offer").delete().eq("id", offer.id);
      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: offersQueryKey });
      void message.success("Oferta usunięta");
    },
    onError: (error) => {
      void message.error(error instanceof Error ? error.message : "Nie udało się usunąć oferty");
    },
  });

  const offers = offersQuery.data ?? [];

  const openNewOffer = () => {
    setActiveOffer(null);
    form.setFieldsValue({
      title: "",
      description: "",
      price: "",
      type: ClientOfferType.SelfCheckout,
      tier: ClientOfferTier.HighTicket,
      url: "",
      badge: "",
      productIds: [],
      touchpoints: [],
      enabled: true,
      sortOrder: offers.length * 10 + 10,
      audienceJoinedFrom: null,
      audienceJoinedTo: null,
    });
    setEditorOpen(true);
  };

  const openEditOffer = (offer: ClientOfferEntity) => {
    setActiveOffer(offer);
    form.setFieldsValue({
      title: offer.title,
      description: offer.description,
      price: offer.price ?? "",
      type: offer.type as ClientOfferType,
      tier: (offer.tier as ClientOfferTier) ?? ClientOfferTier.HighTicket,
      url: offer.url,
      badge: offer.badge ?? "",
      productIds: toStringArray(offer.productIds),
      touchpoints: toStringArray(offer.touchpoints),
      enabled: offer.enabled,
      sortOrder: offer.sortOrder,
      audienceJoinedFrom: offer.audienceJoinedFrom ? dayjs(offer.audienceJoinedFrom) : null,
      audienceJoinedTo: offer.audienceJoinedTo ? dayjs(offer.audienceJoinedTo) : null,
    });
    setEditorOpen(true);
  };

  return (
    <Card
      title="Oferty dla klientek"
      loading={offersQuery.isLoading}
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={openNewOffer}>
          Dodaj
        </Button>
      }
    >
      {offersQuery.isError ? (
        <Alert
          type="warning"
          showIcon
          message="Nie udało się pobrać ofert"
          description={(offersQuery.error as Error).message}
          className="mb-3"
        />
      ) : null}

      <Table<ClientOfferEntity>
        rowKey="id"
        dataSource={offers}
        pagination={false}
        size="middle"
        columns={[
          {
            title: "Kolejność",
            dataIndex: "sortOrder",
            width: 90,
          },
          {
            title: "Oferta",
            dataIndex: "title",
            render: (_, offer) => (
              <div className="min-w-0">
                <Space size={6}>
                  <Typography.Text strong>{offer.title}</Typography.Text>
                  {offer.badge ? <Tag color="gold">{offer.badge}</Tag> : null}
                </Space>
                {offer.description ? (
                  <Typography.Paragraph type="secondary" className="!mb-0 text-xs" ellipsis={{ rows: 2 }}>
                    {offer.description}
                  </Typography.Paragraph>
                ) : null}
              </div>
            ),
          },
          {
            title: "Typ",
            dataIndex: "type",
            width: 130,
            render: (type: string) =>
              type === (ClientOfferType.Consultation as string) ? (
                <Tag color="purple">Konsultacja</Tag>
              ) : (
                <Tag color="blue">Self-checkout</Tag>
              ),
          },
          {
            title: "Stopień",
            dataIndex: "tier",
            width: 120,
            render: (tier: string) =>
              tier === (ClientOfferTier.LowTicket as string) ? (
                <Tag color="green">Low ticket</Tag>
              ) : (
                <Tag color="gold">High ticket</Tag>
              ),
          },
          {
            title: "Cena",
            dataIndex: "price",
            width: 100,
            render: (price: string | null) => price || <Typography.Text type="secondary">—</Typography.Text>,
          },
          {
            title: "Miejsca",
            dataIndex: "touchpoints",
            width: 200,
            render: (_, offer) => {
              const touchpoints = toStringArray(offer.touchpoints);
              if (!touchpoints.length) return <Tag>Wszystkie</Tag>;
              return (
                <Space size={4} wrap>
                  {touchpoints.map((touchpoint) => (
                    <Tag key={touchpoint}>{touchpointLabel(touchpoint)}</Tag>
                  ))}
                </Space>
              );
            },
          },
          {
            title: "Produkty",
            dataIndex: "productIds",
            width: 200,
            render: (_, offer) => {
              const productIds = toStringArray(offer.productIds);
              if (!productIds.length) return <Tag>Wszystkie</Tag>;
              return (
                <Space size={4} wrap>
                  {productIds.map((productId) => (
                    <Tag key={productId}>{productNameById.get(productId) ?? productId}</Tag>
                  ))}
                </Space>
              );
            },
          },
          {
            title: "Aktywna",
            dataIndex: "enabled",
            width: 90,
            render: (_, offer) => (
              <Switch
                size="small"
                checked={offer.enabled}
                loading={toggleOfferMutation.isLoading}
                onChange={(enabled) => toggleOfferMutation.mutate({ offer, enabled })}
              />
            ),
          },
          {
            title: "Akcje",
            key: "actions",
            width: 180,
            render: (_, offer) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEditOffer(offer)}>
                  Edytuj
                </Button>
                <Popconfirm
                  title="Usunąć ofertę?"
                  okText="Usuń"
                  cancelText="Anuluj"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => deleteOfferMutation.mutate(offer)}
                  disabled={deleteOfferMutation.isLoading}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} disabled={deleteOfferMutation.isLoading}>
                    Usuń
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <Drawer
        title={activeOffer ? "Edytuj ofertę" : "Nowa oferta"}
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        width={620}
        extra={
          <Button type="primary" loading={saveOfferMutation.isLoading} onClick={() => form.submit()}>
            Zapisz
          </Button>
        }
      >
        <Form form={form} layout="vertical" onFinish={(values) => saveOfferMutation.mutate(values)}>
          <Form.Item name="title" label="Tytuł" rules={[{ required: true, message: "Podaj tytuł oferty" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Opis">
            <Input.TextArea rows={3} />
          </Form.Item>
          <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
            <Form.Item name="type" label="Typ" rules={[{ required: true }]}>
              <Select options={offerTypeOptions} />
            </Form.Item>
            <Form.Item name="price" label="Cena (etykieta, np. 297 zł)">
              <Input placeholder="np. 297 zł" />
            </Form.Item>
          </div>
          <Form.Item
            name="tier"
            label="Stopień ścieżki upgrade"
            rules={[{ required: true }]}
            extra="Na ekranie głównym oferty układają się w kroki: najpierw low ticket, potem high ticket. Konsultacja to high ticket."
          >
            <Select options={offerTierOptions} />
          </Form.Item>
          <Form.Item name="url" label="URL" rules={[{ required: true, message: "Podaj adres URL oferty" }]}>
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item name="badge" label="Badge (np. Bestseller)">
            <Input />
          </Form.Item>
          <Form.Item
            name="productIds"
            label="Produkty (puste = oferta dla wszystkich produktów)"
            extra={productsQuery.isError ? "Nie udało się pobrać listy produktów" : undefined}
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              loading={productsQuery.isLoading}
              options={products.map((product) => ({ value: product.id, label: product.name }))}
            />
          </Form.Item>
          <Form.Item name="touchpoints" label="Miejsca wyświetlania (puste = wszystkie)">
            <Select mode="multiple" allowClear options={touchpointOptions} />
          </Form.Item>
          <Alert
            type="info"
            showIcon
            className="mb-3"
            message="Okno kohorty (opcjonalne)"
            description="Ogranicza ofertę do klientek, które dołączyły (data zakupu produktu) w podanym zakresie. Puste = wszystkie klientki z pasującym produktem. Dzięki temu nową ofertę zobaczą tylko klientki nowego flow, a nie te z tym samym produktem sprzed zmiany."
          />
          <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
            <Form.Item name="audienceJoinedFrom" label="Dołączyły od">
              <DatePicker className="w-full" format="YYYY-MM-DD" allowClear placeholder="bez dolnej granicy" />
            </Form.Item>
            <Form.Item name="audienceJoinedTo" label="Dołączyły do">
              <DatePicker className="w-full" format="YYYY-MM-DD" allowClear placeholder="bez górnej granicy" />
            </Form.Item>
          </div>
          <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
            <Form.Item name="sortOrder" label="Kolejność">
              <InputNumber className="w-full" />
            </Form.Item>
            <Form.Item name="enabled" label="Aktywna" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
        </Form>
      </Drawer>
    </Card>
  );
};
